import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import SlidePanel from "../../components/SlidePanel/SlidePanel";
import AppDropdown from "../../components/AppDropdown/AppDropdown";
import * as hrmsService from "../../services/hrmsService";
import { FormFields, cleanValues, useMasterOptions } from "./hrForm";
import { Field, LoadState, PrimaryButton, SecondaryButton, Tabs } from "./hrComponents";
import { JOB_MASTER_KEYS, LITE_JOB_MASTER_KEYS, roleOptionsFor, employmentFields, jobFields, liteEmploymentFields, liteJobFields, personalFields } from "./employeeFields";
import { resetDirectoryCache } from "./EmployeeSelect";
import { useLoad } from "./hrUi";

// EMP-05: two ways to add a person. A NEW person gets a login (their mobile number is how they sign in);
// someone already on the platform (team member, crew, the owner) is simply ENABLED as an employee.
const MODE = { NEW: "new", EXISTING: "existing" };

// `lite`: the Employees tier - fewer fields (see employeeFields.js), nothing that needs the full HR module.
export default function EmployeeAddDrawer({ show, onHide, onDone, lite = false, canMakeAdmin = true }) {
  const [mode, setMode] = useState(MODE.NEW);
  const [values, setValues] = useState({});
  const [login, setLogin] = useState({});
  const [existing, setExisting] = useState(null);
  const [saving, setSaving] = useState(false);
  const options = useMasterOptions(lite ? LITE_JOB_MASTER_KEYS : JOB_MASTER_KEYS);
  const candidates = useLoad(() => hrmsService.getEmployeeCandidates(), [show], { enabled: show });

  useEffect(() => {
    if (show) {
      setValues({});
      setLogin({});
      setExisting(null);
      setMode(MODE.NEW);
    }
  }, [show]);

  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));
  const person = (candidates.data || []).find((c) => c.user_id === existing);

  const save = async () => {
    setSaving(true);
    try {
      const body = cleanValues(values);
      let res;
      if (mode === MODE.NEW) {
        if (!login.phone) return toast.error("Enter the mobile number they will sign in with");
        if (!login.role) return toast.error("Choose their role: Admin, Support, HR or Crew");
        res = await hrmsService.createEmployee({ ...body, login: cleanValues(login) });
      } else {
        if (!existing) return toast.error("Choose the person to enable");
        res = await hrmsService.enableEmployee(existing, body);
      }
      if (res?.success) {
        toast.success(res.message || "Employee added");
        resetDirectoryCache(); // so this person shows up in every picker (Reports to, etc.) right away, not after a reload
        onDone(res.data);
        onHide();
      } else toast.error(hrmsService.responseMessage(res));
    } finally {
      setSaving(false);
    }
  };

  const personal = personalFields().filter((f) => (mode === MODE.EXISTING ? f.key !== "full_name" : true));

  return (
    <SlidePanel
      show={show}
      onHide={() => !saving && onHide()}
      title="Add employee"
      width={640}
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={saving}>
            Add employee
          </PrimaryButton>
        </>
      }
    >
      <Tabs
        active={mode}
        onChange={setMode}
        tabs={[
          { key: MODE.NEW, label: "New person" },
          { key: MODE.EXISTING, label: "Already on the platform" },
        ]}
      />

      {mode === MODE.NEW ? (
        <>
          <h6 className="mb-2">Login</h6>
          <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 12 }}>They sign in with an OTP sent to this mobile number.</div>
          <FormFields
            columns={2}
            values={login}
            onChange={(k, v) => setLogin((s) => ({ ...s, [k]: v }))}
            fields={[
              { key: "phone", label: "Mobile number", type: "mobile", required: true },
              { key: "email", label: "Login email", type: "email", max: 45 },
              { key: "role", label: "Role", type: "select", required: true, placeholder: "Choose a role", options: roleOptionsFor(canMakeAdmin), hint: "What this login can do on the platform - separate from their HR permissions, set later under HR Settings > Access." },
            ]}
          />
          {Number(login.role) === 7 ? (
            <div className="mb-3" style={{ fontSize: 13, color: "#1E40AF", background: "#EFF6FF", borderRadius: 8, padding: "10px 14px" }} role="status">
              HR staff see only the HR screens, and can manage people, leave and approvals. Payroll, finance approval and attendance admin are granted separately.
            </div>
          ) : null}
          {Number(login.role) === 5 ? (
            <div className="mb-3" style={{ fontSize: 13, color: "#1E40AF", background: "#EFF6FF", borderRadius: 8, padding: "10px 14px" }} role="status">
              Crew take bookings. After adding them, attach their services, hours and fee from the Crew screen (Add), so they can be booked.
            </div>
          ) : null}
          {Number(login.role) === 2 ? (
            <div className="mb-3" style={{ fontSize: 13, color: "#92400E", background: "#FEF3C7", borderRadius: 8, padding: "10px 14px" }} role="alert">
              "Admin" gives this person full administrator access to your whole account - every screen, not just HR. Choose "Team member (Support)" unless they genuinely need that.
            </div>
          ) : null}
        </>
      ) : (
        <LoadState loading={candidates.loading} error={candidates.error} onRetry={candidates.reload} rows={2}>
          <Field label="Person" required hint={(candidates.data || []).length ? undefined : "Everyone on your team is already an employee."}>
            <AppDropdown
              value={existing}
              options={(candidates.data || []).map((c) => ({ label: `${c.full_name}${c.phone_number ? ` · ${c.phone_number}` : ""}`, value: c.user_id }))}
              onChange={(e) => setExisting(e.value)}
              placeholder="Select person"
              filter
            />
          </Field>
          {person ? <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 12 }}>Their name, email and phone come from their platform profile.</div> : null}
        </LoadState>
      )}

      <h6 className="mt-3 mb-2">Personal</h6>
      <FormFields columns={2} values={values} onChange={set} fields={personal} />
      <h6 className="mt-2 mb-2">Employment</h6>
      <FormFields columns={2} values={values} onChange={set} fields={lite ? liteEmploymentFields() : employmentFields()} />
      <h6 className="mt-2 mb-2">Job</h6>
      <FormFields columns={2} values={values} onChange={set} fields={lite ? liteJobFields(options) : jobFields(options)} />
    </SlidePanel>
  );
}
